// What changed between two versions of a question (ТЗ 3.2, the Versions tab): which of its fields, and word by
// word in its rich text (the editor's or a .docx import's HTML, with KaTeX math and pictures).

// The pieces a rich text is compared by. Kept whole: a tag (its attribute values may hold ">"), math as
// KaTeXComponent finds it (\[ … \], \( … \), $ … $; never across a tag), a LaTeX command written outside math
// (\rightarrow, which the page turns into an arrow) and an entity. Apart: each word, run of spaces and other
// character.
const TOKEN = new RegExp(
  [
    `<[a-zA-Z/!](?:[^>"']|"[^"]*"|'[^']*')*>`,
    String.raw`\\\[(?:(?!\\\])[^<])*\\\]`,
    String.raw`\\\((?:(?!\\\))[^<])*\\\)`,
    String.raw`\$[^$<\n]*\$`,
    String.raw`\\[a-zA-Z]+`,
    String.raw`&(?:#\d+|#x[\da-fA-F]+|[a-zA-Z][a-zA-Z\d]*);`,
    String.raw`\s+`,
    String.raw`[\p{L}\p{M}\p{N}]+`,
    String.raw`[\s\S]`,
  ].join("|"),
  "gu"
);

export const tokenize = (html) => (typeof html === "string" && html ? html.match(TOKEN) || [] : []);

const isTag = (token) => token[0] === "<" && token.length > 1 && token[token.length - 1] === ">";
// A picture is content: a changed one is marked like a changed word. Other tags only shape the text.
const isPicture = (token) => /^<img\b/i.test(token);
const isBlank = (text) => !text.replace(/&nbsp;|&#160;|\s/g, "");
// Spaces and punctuation: what a rewrite leaves in place between its changes.
const isFiller = (token) => /^(?:\s+|&nbsp;|&#160;|[^\p{L}\p{M}\p{N}<>$\\&])$/u.test(token);

// Past this many cells (old pieces × new pieces left between their common start and end) the two texts are
// too far apart to pair word by word: what lies between counts as replaced whole.
const MAX_CELLS = 2500000;

/**
 * [{op: "equal" | "delete" | "insert", token}] that turns the pieces `a` into `b`: their longest common
 * subsequence. Spaces and punctuation left alone between two changes count as changed too, so a rewritten
 * phrase reads as one change rather than word by word.
 */
export function diffTokens(a, b) {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }

  const ops = a.slice(0, start).map((token) => ({ op: "equal", token }));
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const n = midA.length;
  const m = midB.length;
  if (n && m && n * m <= MAX_CELLS) {
    // Compared as numbers: a picture's tag may carry megabytes of data.
    const ids = new Map();
    const idOf = (token) => {
      if (!ids.has(token)) ids.set(token, ids.size);
      return ids.get(token);
    };
    const x = midA.map(idOf);
    const y = midB.map(idOf);
    // lcs[i * width + j]: the length of the longest common subsequence of x[i..] and y[j..]; it never exceeds
    // min(n, m) <= sqrt(MAX_CELLS), so 16 bits hold it.
    const width = m + 1;
    const lcs = new Uint16Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i * width + j] =
          x[i] === y[j] ? lcs[(i + 1) * width + j + 1] + 1 : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (x[i] === y[j]) {
        ops.push({ op: "equal", token: midA[i++] });
        j++;
      } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
        ops.push({ op: "delete", token: midA[i++] });
      } else {
        ops.push({ op: "insert", token: midB[j++] });
      }
    }
    while (i < n) ops.push({ op: "delete", token: midA[i++] });
    while (j < m) ops.push({ op: "insert", token: midB[j++] });
  } else {
    midA.forEach((token) => ops.push({ op: "delete", token }));
    midB.forEach((token) => ops.push({ op: "insert", token }));
  }
  a.slice(endA).forEach((token) => ops.push({ op: "equal", token }));
  return mergeGaps(ops);
}

function mergeGaps(ops) {
  const merged = [];
  let i = 0;
  while (i < ops.length) {
    if (ops[i].op !== "equal") {
      merged.push(ops[i++]);
      continue;
    }
    let j = i;
    while (j < ops.length && ops[j].op === "equal") j++;
    const run = ops.slice(i, j);
    if (i > 0 && j < ops.length && run.every(({ token }) => isFiller(token))) {
      run.forEach(({ token }) => merged.push({ op: "delete", token }));
      run.forEach(({ token }) => merged.push({ op: "insert", token }));
    } else {
      merged.push(...run);
    }
    i = j;
  }
  return merged;
}

// How each side marks its changes: the old text what it lost, the new one what it gained. Tailwind classes, so
// they are written out whole here.
const MARKS = {
  old: {
    op: "delete",
    tag: "del",
    text: "rounded-sm bg-red-100 px-0.5 text-red-900 line-through decoration-red-500/60",
    picture: "inline-block rounded opacity-60 outline outline-2 outline-offset-2 outline-red-500",
  },
  new: {
    op: "insert",
    tag: "ins",
    text: "rounded-sm bg-emerald-100 px-0.5 text-emerald-900 no-underline",
    picture: "inline-block rounded outline outline-2 outline-offset-2 outline-emerald-500",
  },
};

// The look of a removed and of an added piece of text, for marks drawn outside HTML (a legend, plain text).
export const MARK_CLASSES = { old: MARKS.old.text, new: MARKS.new.text };

/**
 * One side ("old" or "new") of a diff as HTML for RichText: the text it shows with its own changes wrapped in
 * <del> (old) or <ins> (new). Tags stay outside the marks, so the HTML keeps its structure; a change of spaces
 * alone is not marked (the page does not show it).
 */
export function markChanges(ops, side) {
  const { op: shown, tag, text, picture } = MARKS[side];
  let html = "";
  let run = "";
  const flush = () => {
    if (run) html += isBlank(run) ? run : `<${tag} class="${text}">${run}</${tag}>`;
    run = "";
  };
  ops.forEach(({ op, token }) => {
    if (op !== "equal" && op !== shown) return;
    if (op === "equal" || (isTag(token) && !isPicture(token))) {
      flush();
      html += token;
    } else if (isPicture(token)) {
      flush();
      html += `<${tag} class="${picture}">${token}</${tag}>`;
    } else {
      run += token;
    }
  });
  flush();
  return html;
}

// Whether words, math or pictures changed, rather than only the formatting (tags) or spaces.
export const wordsChanged = (ops) =>
  ops.some(({ op, token }) => op !== "equal" && (isPicture(token) || (!isTag(token) && !isBlank(token))));

// Both sides of a rich text's diff: {old, new} HTML and whether more than the formatting changed.
export function diffRichText(before, after) {
  const ops = diffTokens(tokenize(before || ""), tokenize(after || ""));
  return { old: markChanges(ops, "old"), new: markChanges(ops, "new"), words: wordsChanged(ops) };
}

// One side of a diff as runs of text, [{text, changed}], for plain text drawn by React (a question's name).
const textRuns = (ops, side) => {
  const runs = [];
  ops.forEach(({ op, token }) => {
    if (op !== "equal" && op !== MARKS[side].op) return;
    const changed = op !== "equal";
    const last = runs[runs.length - 1];
    if (last && last.changed === changed) last.text += token;
    else runs.push({ text: token, changed });
  });
  return runs;
};

export function diffPlainText(before, after) {
  const ops = diffTokens(tokenize(before || ""), tokenize(after || ""));
  return { old: textRuns(ops, "old"), new: textRuns(ops, "new") };
}

// The fields of a version's data in the order the comparison shows them; the catalog ones are compared by id
// (their names in `labels` may be renamed since).
export const VERSION_FIELDS = ["body", "answer_keys", "subtopic", "difficulty", "purpose", "objective", "name"];

// The answer keys in one order whatever order they came in (the server sorts them, two may share a letter).
const keyList = (keys) =>
  (keys || [])
    .map(({ option, body, is_correct }) => JSON.stringify([option ?? "", body ?? "", !!is_correct]))
    .sort()
    .join("\n");

export function fieldChanged(field, a, b) {
  switch (field) {
    case "body":
    case "name":
      return (a?.[field] || "") !== (b?.[field] || "");
    case "answer_keys":
      return keyList(a?.answer_keys) !== keyList(b?.answer_keys);
    case "difficulty":
      return (a?.difficulty || null) !== (b?.difficulty || null);
    default:
      return (a?.[`${field}_id`] || null) !== (b?.[`${field}_id`] || null);
  }
}

const byLetter = (x, y) => (x < y ? -1 : x > y ? 1 : 0);

/**
 * The answer keys of two versions side by side, paired by letter (the first "A" with the first "A"), in the
 * letters' order: [{key, letter, old, new, change}], `change` is "added" or "removed" for a letter only one side
 * has, "changed" for a new text or a new correct mark, else null.
 */
export function pairAnswerKeys(oldKeys, newKeys) {
  const groups = new Map();
  const add = (side) => (answer) => {
    const letter = answer?.option ?? "";
    if (!groups.has(letter)) groups.set(letter, { old: [], new: [] });
    groups.get(letter)[side].push(answer);
  };
  (oldKeys || []).forEach(add("old"));
  (newKeys || []).forEach(add("new"));
  const rows = [];
  [...groups.keys()].sort(byLetter).forEach((letter) => {
    const group = groups.get(letter);
    for (let i = 0; i < Math.max(group.old.length, group.new.length); i++) {
      const before = group.old[i] || null;
      const after = group.new[i] || null;
      const change = !before
        ? "added"
        : !after
        ? "removed"
        : before.body !== after.body || !!before.is_correct !== !!after.is_correct
        ? "changed"
        : null;
      rows.push({ key: `${letter}-${i}`, letter, old: before, new: after, change });
    }
  });
  return rows;
}

// The letters marked correct, in order: ["B"].
export const correctLetters = (keys) =>
  (keys || [])
    .filter((answer) => answer.is_correct)
    .map((answer) => answer.option)
    .sort(byLetter);
